import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://nirvikalp.integrashion.com/api';

export default function GlobalMasters({ db, setDb, saveToDB }) {
  const [activeCat, setActiveCat] = useState("brands");
  const [editId, setEditId]       = useState(null);
  const [val, setVal]             = useState("");

  // Default LC factors (garment industry 85% learning curve)
  const DEFAULT_LC_FACTORS = [0.60, 0.70, 0.78, 0.84, 0.89, 0.94];
  const LC_TIER_LABELS = ['< 300 pcs', '300–600 pcs', '600–1 200 pcs', '1 200–3 000 pcs', '3 000–6 000 pcs', '> 6 000 pcs'];

  const [zoneIn, setZoneIn] = useState({
    country: "", region: "", name: "", cpm: "",
    peakEff: "85",
    lcFactors: [...DEFAULT_LC_FACTORS],
  });
  const [zoneEditId, setZoneEditId] = useState(null);

  const [currIn, setCurrIn] = useState({ name: "", rate: "", isBase: false });
  const [currEditId, setCurrEditId] = useState(null);

  // ── FX status state ──
  const [fxStatus, setFxStatus] = useState({
    loading: false,
    lastUpdated: null,   // ISO timestamp string
    error: null,
    saving: false,
  });

  // ─────────────────────────────────────────────────────────────
  //  HELPERS
  // ─────────────────────────────────────────────────────────────
  const getList = (key) => db[key] || db[key.slice(0, -1)] || [];

  const typeMap = {
    brands: 'BRAND', buyers: 'BUYER', seasons: 'SEASON',
    products: 'PRODUCT', fabrics: 'FABRIC'
  };

  const apiSave = async (category, type, data) => {
    try {
      const res = await fetch(`${API_BASE}/save_universal.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, type, data })
      });
      const text = await res.text();
      try { return JSON.parse(text); } catch (_) { return { success: false, error: text }; }
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const apiDelete = async (category, type, id) => {
    return apiSave(category, type, { id, _deleted: true });
  };

  // ─────────────────────────────────────────────────────────────
  //  MASTER ITEMS  (brands, buyers, seasons, products, fabrics)
  // ─────────────────────────────────────────────────────────────
  const saveMaster = async () => {
    if (!val.trim()) return alert("Enter Name");
    const dbType = typeMap[activeCat] || activeCat.toUpperCase().slice(0, -1);
    const payload = editId
      ? { id: editId, name: val.trim(), type: dbType }
      : { name: val.trim(), type: dbType };

    const result = await apiSave('common', dbType, payload);
    if (!result.id) return alert("Save failed: " + (result.error || JSON.stringify(result)));

    const newItem = { id: result.id, name: val.trim() };
    setDb(prev => {
      const list = getList(activeCat);
      const updated = editId
        ? list.map(x => x.id === editId ? newItem : x)
        : [...list, newItem];
      return { ...prev, [activeCat]: updated };
    });
    setVal(""); setEditId(null);
  };

  const editMaster = (item) => { setVal(item.name); setEditId(item.id); };

  const delMaster = async (id) => {
    if (!window.confirm("Delete?")) return;
    const dbType = typeMap[activeCat] || activeCat.toUpperCase().slice(0, -1);
    setDb(prev => ({ ...prev, [activeCat]: getList(activeCat).filter(x => x.id !== id) }));
    if (id && Number(id) < 2000000000) await apiDelete('common', dbType, id);
  };

  // ─────────────────────────────────────────────────────────────
  //  CURRENCIES  — individual add / edit
  // ─────────────────────────────────────────────────────────────
  const saveCurrency = async () => {
    if (!currIn.name || !currIn.rate) return alert("Fill all fields");
    const payload = currEditId
      ? {
          id: currEditId,
          name: currIn.name.toUpperCase(),
          rate: parseFloat(currIn.rate),
          isBase: currIn.isBase,
          type: 'CURRENCY',
          // Preserve existing lockedRate if it was already set
          lockedRate: (db.currencies || []).find(c => c.id === currEditId)?.lockedRate ?? null,
          rateLockedAt: (db.currencies || []).find(c => c.id === currEditId)?.rateLockedAt ?? null,
        }
      : {
          name: currIn.name.toUpperCase(),
          rate: parseFloat(currIn.rate),
          isBase: currIn.isBase,
          type: 'CURRENCY',
          lockedRate: parseFloat(currIn.rate),         // first save = lock rate too
          rateLockedAt: new Date().toISOString(),
        };

    const result = await apiSave('common', 'CURRENCY', payload);
    if (!result.id) return alert("Save failed: " + (result.error || JSON.stringify(result)));

    const entry = { ...payload, id: result.id };
    setDb(prev => {
      let list = prev.currencies || [];
      let newList = currEditId
        ? list.map(c => c.id === currEditId ? entry : c)
        : [...list, entry];
      if (currIn.isBase) newList = newList.map(c => ({ ...c, isBase: c.id === entry.id }));
      return { ...prev, currencies: newList };
    });
    setCurrIn({ name: "", rate: "", isBase: false }); setCurrEditId(null);
  };

  const editCurrency = (c) => {
    setCurrIn({ name: c.name, rate: c.rate, isBase: c.isBase ?? false });
    setCurrEditId(c.id);
  };

  const delCurrency = async (id) => {
    if (!window.confirm("Delete currency?")) return;
    setDb(prev => ({ ...prev, currencies: (prev.currencies || []).filter(c => c.id !== id) }));
    if (id && Number(id) < 2000000000) await apiDelete('common', 'CURRENCY', id);
  };

  // ─────────────────────────────────────────────────────────────
  //  LOCK COSTING RATE — snapshot current live rate per currency
  // ─────────────────────────────────────────────────────────────
  const lockCostingRate = async (currency) => {
    if (!window.confirm(`Lock costing rate for ${currency.name} at ${currency.rate}?\nAll NEW costings will use this locked rate until you change it.`)) return;

    const updated = {
      ...currency,
      lockedRate: currency.rate,
      rateLockedAt: new Date().toISOString(),
    };
    const result = await apiSave('common', 'CURRENCY', updated);
    if (!result.id) return alert("Lock failed: " + (result.error || 'Unknown'));

    setDb(prev => ({
      ...prev,
      currencies: (prev.currencies || []).map(c => c.id === currency.id ? updated : c)
    }));
  };

  // ─────────────────────────────────────────────────────────────
  //  LIVE FX RATES — fetch from open.er-api.com and SAVE ALL
  // ─────────────────────────────────────────────────────────────
  // Returns {updatedList, baseCurrency} — used by both button and auto-refresh
  const fetchAndSaveRates = useCallback(async (silent = false) => {
    const currencies = db.currencies || [];
    if (currencies.length === 0) {
      if (!silent) alert("No currencies configured yet. Add currencies first.");
      return;
    }

    // Find base currency to decide which API to call
    const baseCurr = currencies.find(c => c.isBase);
    const base = baseCurr?.name?.toUpperCase() || 'USD';

    setFxStatus(s => ({ ...s, loading: true, error: null }));

    let rates;
    try {
      const res = await fetch(`https://open.er-api.com/v6/latest/${base}`);
      const data = await res.json();
      if (!data?.rates) throw new Error('No rates in response');
      rates = data.rates;
    } catch (e) {
      setFxStatus(s => ({ ...s, loading: false, error: `Fetch failed: ${e.message}` }));
      if (!silent) alert(`Could not fetch rates: ${e.message}`);
      return;
    }

    // Build updated list — only update rate, never touch lockedRate/rateLockedAt
    const updated = currencies.map(c => {
      const liveRate = rates[c.name.toUpperCase()];
      if (liveRate == null) return c; // unknown currency — keep existing
      return { ...c, rate: liveRate };
    });

    // ── Persist ALL currencies in ONE request via bulk endpoint ──
    setFxStatus(s => ({ ...s, loading: false, saving: true }));

    const toSave = updated.filter(c => c.id && Number(c.id) < 2000000000);
    let bulkResult = { success: false, errors: [] };
    try {
      const res = await fetch(`${API_BASE}/save_universal.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'bulk_currency_update', data: { currencies: toSave } })
      });
      const text = await res.text();
      bulkResult = JSON.parse(text);
    } catch (e) {
      bulkResult = { success: false, errors: [e.message] };
    }

    const failed = bulkResult.errors || [];

    const now = new Date().toISOString();
    setFxStatus({
      loading: false,
      saving: false,
      lastUpdated: now,
      error: failed.length > 0 ? `${failed.length} currencies failed to save` : null,
    });

    // Update React state
    setDb(prev => ({ ...prev, currencies: updated }));

    if (!silent) {
      const failMsg = failed.length ? `\n⚠ ${failed.length} currencies failed to save.` : '';
      alert(`✓ Rates updated & saved! Base: ${base}${failMsg}`);
    }
  }, [db.currencies]);

  // ── Auto-refresh rates on mount (once, silently) ──
  useEffect(() => {
    const currencies = db.currencies || [];
    if (currencies.length > 0) {
      fetchAndSaveRates(true); // silent = no alert
    }
  }, []); // eslint-disable-line

  // ─────────────────────────────────────────────────────────────
  //  CHANGE BASE CURRENCY — recalculates all rates relative to new base
  // ─────────────────────────────────────────────────────────────
  const changeBaseCurrency = async (newBaseId) => {
    const currencies = db.currencies || [];
    const newBase = currencies.find(c => c.id === newBaseId);
    if (!newBase) return;
    if (!window.confirm(`Set ${newBase.name} as base currency? All rates will be recalculated relative to ${newBase.name}.`)) return;

    // Mark as base in DB first
    const updated = currencies.map(c => ({ ...c, isBase: c.id === newBaseId }));

    // Save the isBase change
    const savePromises = updated
      .filter(c => c.id && Number(c.id) < 2000000000)
      .map(c => apiSave('common', 'CURRENCY', c));
    await Promise.all(savePromises);

    setDb(prev => ({ ...prev, currencies: updated }));

    // Now fetch fresh rates relative to new base
    await fetchAndSaveRates(false);
  };

  // ─────────────────────────────────────────────────────────────
  //  PRODUCTION ZONES
  // ─────────────────────────────────────────────────────────────
  const saveZone = async () => {
    if (!zoneIn.name) return alert("Zone Name is required");
    if (!zoneIn.cpm)  return alert("CPM is required");

    const factors = zoneIn.lcFactors.map(f => parseFloat(f) || 0);
    for (let i = 0; i < factors.length; i++) {
      if (factors[i] <= 0 || factors[i] > 1)
        return alert(`LC Factor for tier ${i + 1} must be between 0.01 and 1.00`);
      if (i > 0 && factors[i] < factors[i - 1])
        return alert(`LC Factor for tier ${i + 1} must be ≥ tier ${i}`);
    }

    const payload = {
      ...(zoneEditId ? { id: zoneEditId } : {}),
      ...zoneIn,
      cpm: parseFloat(zoneIn.cpm),
      peakEff: parseFloat(zoneIn.peakEff) || 85,
      lcFactors: factors,
      type: 'LOCATION',
    };

    const result = await apiSave('common', 'LOCATION', payload);
    if (!result.id) return alert("Save failed: " + (result.error || JSON.stringify(result)));

    const entry = { ...payload, id: result.id };
    setDb(prev => {
      const list = prev.productionZones || [];
      const newList = zoneEditId
        ? list.map(z => z.id === zoneEditId ? entry : z)
        : [...list, entry];
      return { ...prev, productionZones: newList };
    });
    setZoneIn({ country: "", region: "", name: "", cpm: "", peakEff: "85", lcFactors: [...DEFAULT_LC_FACTORS] });
    setZoneEditId(null);
  };

  const editZone = (z) => {
    setZoneIn({
      country:   z.country   || "",
      region:    z.region    || "",
      name:      z.name      || "",
      cpm:       z.cpm       || "",
      peakEff:   z.peakEff   || "85",
      lcFactors: z.lcFactors?.length === 6 ? z.lcFactors : [...DEFAULT_LC_FACTORS],
    });
    setZoneEditId(z.id);
  };

  const delZone = async (id) => {
    if (!window.confirm("Delete zone?")) return;
    setDb(prev => ({ ...prev, productionZones: (prev.productionZones || []).filter(z => z.id !== id) }));
    if (id && Number(id) < 2000000000) await apiDelete('common', 'LOCATION', id);
  };

  // ─────────────────────────────────────────────────────────────
  //  RENDER: CURRENCIES
  // ─────────────────────────────────────────────────────────────
  const renderCurrencies = () => {
    const currencies = db.currencies || [];
    const baseCurr   = currencies.find(c => c.isBase);

    return (
      <div className="card">

        {/* ── Header row ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'16px', flexWrap:'wrap', gap:'10px' }}>
          <div>
            <h3 style={{ margin:0 }}>CURRENCY EXCHANGE</h3>
            {baseCurr && (
              <div style={{ fontSize:'12px', color:'#2563eb', marginTop:'4px', fontWeight:'600' }}>
                Base Currency: <strong>{baseCurr.name}</strong>
                {' · '}All rates relative to {baseCurr.name}
              </div>
            )}
          </div>

          {/* Status + Refresh button cluster */}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:'6px' }}>
            <button
              className="btn-p"
              style={{
                background: fxStatus.loading || fxStatus.saving ? '#94a3b8' : '#f59e0b',
                minWidth:'180px', display:'flex', alignItems:'center', gap:'8px', justifyContent:'center'
              }}
              onClick={() => fetchAndSaveRates(false)}
              disabled={fxStatus.loading || fxStatus.saving}
            >
              {fxStatus.loading ? '⏳ Fetching Rates…'
               : fxStatus.saving ? '💾 Saving to DB…'
               : '⟳ REFRESH & SAVE ALL RATES'}
            </button>

            {/* Last updated timestamp */}
            {fxStatus.lastUpdated && (
              <div style={{ fontSize:'10px', color:'#64748b' }}>
                ✓ Last saved: {new Date(fxStatus.lastUpdated).toLocaleString()}
              </div>
            )}
            {fxStatus.error && (
              <div style={{ fontSize:'10px', color:'#ef4444' }}>
                ⚠ {fxStatus.error}
              </div>
            )}
          </div>
        </div>

        {/* ── Auto-refresh notice ── */}
        <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'6px', padding:'8px 12px', marginBottom:'16px', fontSize:'11px', color:'#1d4ed8', lineHeight:'1.6' }}>
          <strong>Auto-refresh:</strong> Rates are fetched from the live FX API and saved to the database every time this page loads.
          Use <strong>Lock for Costing</strong> (🔒) per row to snapshot a rate for use in costings — the locked rate never changes unless you manually re-lock it.
        </div>

        {/* ── Add / Edit form ── */}
        <div className="grid-3" style={{ background:'#f8fafc', padding:'15px', marginBottom:'20px', borderRadius:'8px' }}>
          <div className="field">
            <label>Currency Code</label>
            <input placeholder="e.g. EUR" value={currIn.name} onChange={e => setCurrIn({ ...currIn, name: e.target.value.toUpperCase() })} />
          </div>
          <div className="field">
            <label>Rate vs {baseCurr?.name || 'USD'}</label>
            <input type="number" step="0.0001" placeholder="1.0" value={currIn.rate} onChange={e => setCurrIn({ ...currIn, rate: e.target.value })} />
          </div>
          <div className="field" style={{ justifyContent:'center' }}>
            <label><input type="checkbox" checked={currIn.isBase} onChange={e => setCurrIn({ ...currIn, isBase: e.target.checked })} />  Set as Base Currency</label>
          </div>
          <button className="btn-p" style={{ gridColumn:'span 3' }} onClick={saveCurrency}>
            {currEditId ? "✓ UPDATE CURRENCY" : "+ ADD CURRENCY"}
          </button>
          {currEditId && (
            <button style={{ gridColumn:'span 3', padding:'8px', background:'#f1f5f9', border:'none', borderRadius:'6px', cursor:'pointer', color:'#64748b', fontWeight:'600' }}
              onClick={() => { setCurrEditId(null); setCurrIn({ name:"", rate:"", isBase:false }); }}>
              Cancel
            </button>
          )}
        </div>

        {/* ── Currency Table ── */}
        {currencies.length === 0 ? (
          <div style={{ textAlign:'center', padding:'30px', color:'#94a3b8', border:'2px dashed #e2e8f0', borderRadius:'8px' }}>
            No currencies added yet.
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table className="npd-table">
              <thead>
                <tr>
                  <th>CODE</th>
                  <th>LIVE RATE</th>
                  <th title="Rate locked for use in all new costings — never auto-changed by refresh">🔒 COSTING RATE</th>
                  <th>LOCKED ON</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {currencies.map(c => {
                  const liveRate   = parseFloat(c.rate)       || 0;
                  const locked     = parseFloat(c.lockedRate) || liveRate;
                  const drift      = liveRate > 0 ? Math.abs(((liveRate - locked) / locked) * 100) : 0;
                  const hasDrift   = drift > 0.5; // flag if >0.5% drift from locked

                  return (
                    <tr key={c.id}>
                      {/* Code */}
                      <td style={{ fontWeight:'800', fontSize:'14px' }}>
                        {c.name}
                        {c.isBase && (
                          <span style={{ marginLeft:'6px', fontSize:'9px', background:'#dbeafe', color:'#1d4ed8', padding:'1px 6px', borderRadius:'10px', fontWeight:'800' }}>
                            BASE
                          </span>
                        )}
                      </td>

                      {/* Live rate */}
                      <td style={{ fontFamily:'monospace', fontWeight:'700', color:'#0369a1' }}>
                        {liveRate.toFixed(4)}
                      </td>

                      {/* Costing (locked) rate */}
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <span style={{ fontFamily:'monospace', fontWeight:'800', color: hasDrift ? '#b45309' : '#059669', fontSize:'13px' }}>
                            {locked.toFixed(4)}
                          </span>
                          {hasDrift && (
                            <span title={`Live rate drifted ${drift.toFixed(1)}% from locked costing rate`}
                              style={{ fontSize:'10px', background:'#fef3c7', color:'#b45309', padding:'1px 6px', borderRadius:'10px', fontWeight:'700', cursor:'help' }}>
                              ±{drift.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Locked on date */}
                      <td style={{ fontSize:'11px', color:'#64748b' }}>
                        {c.rateLockedAt
                          ? new Date(c.rateLockedAt).toLocaleDateString()
                          : <span style={{ color:'#cbd5e1' }}>—</span>}
                      </td>

                      {/* Status */}
                      <td>
                        {c.isBase ? (
                          <span style={{ background:'#dbeafe', color:'#1d4ed8', padding:'2px 8px', borderRadius:'10px', fontSize:'11px', fontWeight:'700' }}>
                            BASE
                          </span>
                        ) : (
                          <button
                            title="Set this currency as base — all other rates will be recalculated relative to it"
                            style={{ fontSize:'10px', padding:'2px 8px', background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:'10px', cursor:'pointer', color:'#64748b', fontWeight:'600' }}
                            onClick={() => changeBaseCurrency(c.id)}>
                            Set as Base
                          </button>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{ display:'flex', gap:'6px', alignItems:'center', flexWrap:'wrap' }}>
                        <button className="btn-edit" onClick={() => editCurrency(c)}>Edit</button>
                        <button
                          title={`Lock costing rate at current live rate (${liveRate.toFixed(4)})`}
                          style={{
                            padding:'4px 8px', fontSize:'11px', fontWeight:'700', cursor:'pointer', borderRadius:'4px',
                            background: hasDrift ? '#fef3c7' : '#f0fdf4',
                            color: hasDrift ? '#b45309' : '#15803d',
                            border: hasDrift ? '1px solid #f59e0b' : '1px solid #bbf7d0'
                          }}
                          onClick={() => lockCostingRate(c)}>
                          🔒 Lock
                        </button>
                        <button className="btn-del" onClick={() => delCurrency(c.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Legend */}
            <div style={{ marginTop:'12px', display:'flex', gap:'16px', flexWrap:'wrap', fontSize:'11px', color:'#64748b' }}>
              <span><span style={{ color:'#059669', fontWeight:'700' }}>Green rate</span> = costing rate is in sync with live</span>
              <span><span style={{ color:'#b45309', fontWeight:'700' }}>Amber rate</span> = live rate has drifted &gt;0.5% from locked costing rate — consider re-locking</span>
              <span>🔒 <strong>Lock</strong> = snapshot today's live rate for all new costings</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  //  RENDER: ZONES
  // ─────────────────────────────────────────────────────────────
  const renderZones = () => {
    const previewSMV = 12;
    const previewCPM = parseFloat(zoneIn.cpm) || 0;
    const peakEff    = parseFloat(zoneIn.peakEff) || 85;

    const updateLcFactor = (tierIdx, v) => {
      const updated = [...zoneIn.lcFactors];
      updated[tierIdx] = parseFloat(v) || 0;
      setZoneIn({ ...zoneIn, lcFactors: updated });
    };

    return (
      <div className="card">
        <h3 style={{ marginBottom:'4px' }}>PRODUCTION ZONES & LEARNING CURVES</h3>
        <p style={{ fontSize:'12px', color:'#64748b', marginBottom:'20px', lineHeight:'1.6' }}>
          Each zone stores a <strong>CPM at 100% efficiency</strong> and a <strong>per-tier Learning Curve Factor</strong>.
          When costing a style, the system picks the tier matching your order qty,
          then calculates: <code style={{ background:'#f1f5f9', padding:'1px 6px', borderRadius:'3px' }}>Achievable Eff = Peak Eff × LC Factor</code>,
          and finally: <code style={{ background:'#f1f5f9', padding:'1px 6px', borderRadius:'3px' }}>CM = SMV × CPM@100% ÷ (Eff ÷ 100)</code>.
        </p>

        <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'10px', padding:'20px', marginBottom:'24px' }}>
          <div style={{ fontWeight:'800', fontSize:'12px', color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'16px' }}>
            {zoneEditId ? '✏ Editing Zone' : '+ Add New Zone'}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 2fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div className="field">
              <label>Zone Name *</label>
              <input value={zoneIn.name} onChange={e => setZoneIn({ ...zoneIn, name: e.target.value })} placeholder="e.g. Tirupur Cluster" />
            </div>
            <div className="field">
              <label>Country *</label>
              <input value={zoneIn.country} onChange={e => setZoneIn({ ...zoneIn, country: e.target.value })} placeholder="India" />
            </div>
            <div className="field">
              <label>Region / City</label>
              <input value={zoneIn.region} onChange={e => setZoneIn({ ...zoneIn, region: e.target.value })} placeholder="Tamil Nadu" />
            </div>
            <div className="field">
              <label>CPM @ 100% *</label>
              <input type="number" step="0.0001" value={zoneIn.cpm} onChange={e => setZoneIn({ ...zoneIn, cpm: e.target.value })} placeholder="0.0120" />
            </div>
          </div>

          <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:'20px', alignItems:'center' }}>
              <div className="field" style={{ margin:0 }}>
                <label style={{ color:'#1d4ed8' }}>Factory Peak Efficiency % *</label>
                <input type="number" min="10" max="99" step="0.5" value={zoneIn.peakEff}
                  onChange={e => setZoneIn({ ...zoneIn, peakEff: e.target.value })}
                  style={{ borderColor:'#3b82f6', background:'white', fontWeight:'bold', color:'#1d4ed8', fontSize:'1.1rem' }}/>
              </div>
              <div style={{ fontSize:'12px', color:'#1d4ed8', lineHeight:'1.7' }}>
                <strong>What is this?</strong> The theoretical maximum efficiency this factory / zone can reach on a fully learned style with unlimited quantity.
                Typical values: Bangladesh <strong>65–75%</strong>, India <strong>55–70%</strong>, Vietnam <strong>70–80%</strong>, China Tier-1 <strong>75–85%</strong>.
              </div>
            </div>
          </div>

          <div style={{ background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:'8px', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontWeight:'800', fontSize:'12px', color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'4px' }}>
              Learning Curve Factors by Order Quantity
            </div>
            <div style={{ fontSize:'11px', color:'#7c3aed', marginBottom:'14px' }}>
              Enter as decimal (0.01–1.00).
              <span style={{ marginLeft:'8px', background:'#ede9fe', padding:'1px 8px', borderRadius:'10px', fontWeight:'700' }}>
                Default: garment industry 85% LC
              </span>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'140px 110px 100px 100px 1fr', gap:'8px', marginBottom:'8px', alignItems:'center' }}>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#64748b', textTransform:'uppercase' }}>Order Qty Tier</div>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#64748b', textTransform:'uppercase' }}>LC Factor</div>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#7c3aed', textTransform:'uppercase' }}>Achievable Eff</div>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#059669', textTransform:'uppercase' }}>CM Preview</div>
              <div style={{ fontSize:'10px', fontWeight:'800', color:'#64748b', textTransform:'uppercase' }}>Efficiency Bar</div>
            </div>

            {LC_TIER_LABELS.map((label, i) => {
              const factor    = parseFloat(zoneIn.lcFactors[i]) || DEFAULT_LC_FACTORS[i];
              const achEff    = Math.min(peakEff * factor, 99).toFixed(1);
              const cmPreview = previewSMV > 0 && previewCPM > 0
                ? (previewSMV * previewCPM / (parseFloat(achEff) / 100)).toFixed(3)
                : '—';
              const barPct    = Math.round(parseFloat(achEff));
              const isDefault = Math.abs(factor - DEFAULT_LC_FACTORS[i]) < 0.001;
              const tierColors = ['#ef4444','#f97316','#f59e0b','#84cc16','#22c55e','#16a34a'];
              const color = tierColors[i];

              return (
                <div key={i} style={{
                  display:'grid', gridTemplateColumns:'140px 110px 100px 100px 1fr',
                  gap:'8px', alignItems:'center', marginBottom:'8px',
                  padding:'8px 10px', background:'white', borderRadius:'6px',
                  border:`1px solid ${isDefault ? '#e9d5ff' : '#7c3aed'}`,
                  boxShadow: !isDefault ? '0 0 0 2px rgba(124,58,237,0.1)' : 'none',
                }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:color, flexShrink:0 }}/>
                    <span style={{ fontSize:'11px', fontWeight:'700', color:'#334155' }}>{label}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                    <input type="number" min="0.01" max="1.00" step="0.01"
                      value={zoneIn.lcFactors[i]}
                      onChange={e => updateLcFactor(i, e.target.value)}
                      style={{ width:'70px', padding:'5px 8px', border:`1.5px solid ${isDefault ? '#e9d5ff' : '#7c3aed'}`, borderRadius:'4px', fontWeight:'bold', fontSize:'13px', color:'#4c1d95', background: isDefault ? 'white' : '#faf5ff' }}
                    />
                    {!isDefault && (
                      <button onClick={() => updateLcFactor(i, DEFAULT_LC_FACTORS[i])}
                        title="Reset to default"
                        style={{ fontSize:'10px', color:'#7c3aed', background:'#ede9fe', border:'none', borderRadius:'4px', padding:'3px 6px', cursor:'pointer' }}>
                        ↺
                      </button>
                    )}
                  </div>
                  <div style={{ fontWeight:'800', fontSize:'13px', color:'#4c1d95' }}>
                    {achEff}%
                    <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:'400' }}>{peakEff}% × {(factor*100).toFixed(0)}%</div>
                  </div>
                  <div style={{ fontWeight:'800', fontSize:'13px', color:'#059669' }}>
                    {cmPreview !== '—' ? cmPreview : <span style={{ color:'#cbd5e1' }}>—</span>}
                    {cmPreview !== '—' && <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:'400' }}>SMV {previewSMV}</div>}
                  </div>
                  <div>
                    <div style={{ height:'8px', background:'#f1f5f9', borderRadius:'4px', overflow:'hidden' }}>
                      <div style={{ height:'100%', width:`${barPct}%`, background:`linear-gradient(90deg,${color},#7c3aed)`, borderRadius:'4px', transition:'width 0.3s' }}/>
                    </div>
                    <div style={{ fontSize:'9px', color:'#94a3b8', marginTop:'2px' }}>{barPct}% of 100</div>
                  </div>
                </div>
              );
            })}

            {previewCPM > 0 && (
              <div style={{ marginTop:'8px', fontSize:'11px', color:'#64748b', background:'#f0fdf4', padding:'6px 10px', borderRadius:'4px', border:'1px solid #bbf7d0' }}>
                CM preview uses sample SMV = {previewSMV} min × CPM = {previewCPM}.
              </div>
            )}
            {!previewCPM && (
              <div style={{ marginTop:'8px', fontSize:'11px', color:'#94a3b8' }}>
                Enter CPM above to see live CM preview per tier.
              </div>
            )}

            <button onClick={() => setZoneIn({ ...zoneIn, lcFactors: [...DEFAULT_LC_FACTORS] })}
              style={{ marginTop:'10px', padding:'6px 14px', background:'white', border:'1px solid #e9d5ff', borderRadius:'5px', color:'#7c3aed', fontSize:'11px', cursor:'pointer', fontWeight:'700' }}>
              ↺ Reset All LC Factors to Industry Defaults
            </button>
          </div>

          <div style={{ display:'flex', gap:'10px' }}>
            <button className="btn-p" style={{ flex:1, padding:'12px' }} onClick={saveZone}>
              {zoneEditId ? '✓ UPDATE ZONE' : '+ ADD ZONE'}
            </button>
            {zoneEditId && (
              <button onClick={() => { setZoneEditId(null); setZoneIn({ country:'', region:'', name:'', cpm:'', peakEff:'85', lcFactors:[...DEFAULT_LC_FACTORS] }); }}
                style={{ padding:'12px 20px', background:'#f1f5f9', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'700', color:'#64748b' }}>
                Cancel
              </button>
            )}
          </div>
        </div>

        {(db.productionZones || []).length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8', fontSize:'13px', border:'2px dashed #e2e8f0', borderRadius:'8px' }}>
            No zones added yet.
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table className="npd-table">
              <thead>
                <tr>
                  <th>ZONE NAME</th><th>COUNTRY</th><th>REGION</th>
                  <th>CPM@100%</th><th>PEAK EFF</th>
                  <th style={{ minWidth:'320px' }}>LEARNING CURVE — ACHIEVABLE EFF × TIER</th>
                  <th>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {(db.productionZones || []).map(z => {
                  const factors    = z.lcFactors?.length === 6 ? z.lcFactors : DEFAULT_LC_FACTORS;
                  const peak       = parseFloat(z.peakEff || 85);
                  const tierColors = ['#ef4444','#f97316','#f59e0b','#84cc16','#22c55e','#16a34a'];
                  return (
                    <tr key={z.id}>
                      <td style={{ fontWeight:'700', color:'#1e293b' }}>{z.name}</td>
                      <td>{z.country}</td>
                      <td>{z.region || '—'}</td>
                      <td style={{ fontWeight:'800', color:'#0369a1', fontFamily:'monospace' }}>{z.cpm}</td>
                      <td>
                        <span style={{ background:'#eff6ff', color:'#1d4ed8', padding:'2px 8px', borderRadius:'8px', fontWeight:'800', fontSize:'12px' }}>
                          {peak}%
                        </span>
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
                          {factors.map((f, i) => {
                            const achEff  = Math.min(peak * parseFloat(f), 99).toFixed(0);
                            const isDef   = Math.abs(parseFloat(f) - DEFAULT_LC_FACTORS[i]) < 0.001;
                            return (
                              <div key={i} title={LC_TIER_LABELS[i]}
                                style={{ padding:'2px 7px', borderRadius:'5px', fontSize:'10px', fontWeight:'800',
                                  background: isDef ? '#f1f5f9' : '#faf5ff',
                                  color: isDef ? '#64748b' : '#7c3aed',
                                  border: isDef ? '1px solid #e2e8f0' : '1px solid #c4b5fd', cursor:'default' }}>
                                {achEff}%
                                {!isDef && <span style={{ fontSize:'8px', marginLeft:'2px', opacity:0.7 }}>✎</span>}
                              </div>
                            );
                          })}
                        </div>
                        <div style={{ fontSize:'9px', color:'#94a3b8', marginTop:'3px' }}>{LC_TIER_LABELS.join(' → ')}</div>
                      </td>
                      <td>
                        <button className="btn-edit" onClick={() => editZone(z)}>Edit</button>
                        <button className="btn-del" onClick={() => delZone(z.id)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────
  //  RENDER: GENERIC MASTERS (brands / buyers / seasons / etc.)
  // ─────────────────────────────────────────────────────────────
  const renderMasters = () => (
    <div className="card">
      <h3>MANAGE: {activeCat.toUpperCase()}</h3>
      <div className="form-row">
        <input value={val} onChange={e => setVal(e.target.value)}
          placeholder="Name..." onKeyDown={e => e.key === 'Enter' && saveMaster()} />
        <button className="btn-p" onClick={saveMaster}>{editId ? "UPDATE" : "ADD"}</button>
        {editId && (
          <button style={{ padding:'8px 12px', background:'#f1f5f9', border:'none', borderRadius:'4px', cursor:'pointer' }}
            onClick={() => { setEditId(null); setVal(""); }}>
            Cancel
          </button>
        )}
      </div>
      <table className="npd-table" style={{ marginTop:'20px' }}>
        <thead><tr><th>NAME</th><th>ACTION</th></tr></thead>
        <tbody>{getList(activeCat).map(i => (
          <tr key={i.id}>
            <td>{i.name}</td>
            <td>
              <button className="btn-edit" onClick={() => editMaster(i)}>Edit</button>
              <button className="btn-del" onClick={() => delMaster(i.id)}>Del</button>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );

  // ─────────────────────────────────────────────────────────────
  //  STYLES
  // ─────────────────────────────────────────────────────────────
  const sideBtn = (cat) => ({
    textAlign:'left', width:'100%', padding:'12px 20px', border:'none',
    borderBottom:'1px solid #f1f5f9', cursor:'pointer',
    background: activeCat === cat ? '#eff6ff' : 'transparent',
    color: activeCat === cat ? '#2563eb' : '#64748b',
    borderLeft: activeCat === cat ? '4px solid #2563eb' : '4px solid transparent'
  });

  // ─────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="layout-master">
      <div className="side-rail" style={{ width:'260px', background:'white', borderRight:'1px solid #e2e8f0', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'15px', fontWeight:'bold', background:'#f8fafc', color:'#0f172a', fontSize:'0.75rem' }}>COMMERCIAL MASTERS</div>
        {["brands","buyers","seasons","products","fabrics"].map(cat => (
          <button key={cat} style={sideBtn(cat)} onClick={() => { setActiveCat(cat); setEditId(null); setVal(""); }}>
            {cat.toUpperCase()}
          </button>
        ))}
        <div style={{ padding:'15px', fontWeight:'bold', background:'#f8fafc', color:'#0f172a', fontSize:'0.75rem', borderTop:'1px solid #eee' }}>SETTINGS</div>
        <button style={sideBtn("currencies")} onClick={() => setActiveCat("currencies")}>
          Currency Exchange
          {fxStatus.lastUpdated && (
            <div style={{ fontSize:'9px', color:'#22c55e', marginTop:'1px' }}>✓ Rates synced</div>
          )}
        </button>
        <button style={sideBtn("zones")} onClick={() => setActiveCat("zones")}>Locations / CPM</button>
      </div>

      <div className="content-area" style={{ flex:1, padding:'20px', overflowY:'auto' }}>
        {activeCat === "currencies" ? renderCurrencies()
         : activeCat === "zones"    ? renderZones()
         : renderMasters()}
      </div>
    </div>
  );
}
